<script setup lang="ts">
import ProPreviewCard from '@/components/Search/ProPreviewCard/ProPreviewCard.vue';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { SearchService } from '@/core/sourcing/search/search.service';
import { MessagingService } from '@/core/sourcing/messaging/messaging.service';
import { MessagingSource } from '@/core/sourcing/messaging/types/messaging-source.type';

import SkipTabEmptyState from '@/components/Search/ProSearchResults/SkipTabEmptyState.vue';
import type { SearchGatedUserProfile } from '@/core/sourcing/search/types/search-gated-user-profile.type';
import SelectedCandidateService from '@/core/sourcing/selected-candidate/selected-candidate.service';
import { isProRecentlyActive } from '@/core/sourcing/utils/dates';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ChevronLeftIcon from '@/assets/svg/chevron-left.svg?component';
import ChevronRightIcon from '@/assets/svg/chevron-right.svg?component';
import ProSearchResultsLoader from '@/components/Sourcing/Loader/ProSearchResultsLoader.vue';
import ResumeDialog from '@/components/Shared/Dialogs/ResumeDialog.vue';
import type { ResumeUrlSigner } from '@/composables/useSignedResumeUrl';
import { getNameForCandidate } from '@/core/sourcing/search/utils/get-name-for-candidate.util';
import MeService from '@/core/shared/me/me.service';

const props = defineProps<{
  isLoading: boolean;
  // Whether Talent Search is currently scoped to a job — surfaced on the resume-add-to-job
  // click event so analytics can tell a job-backed search apart from a keyword search.
  hasActiveJob?: boolean;
}>();

const emit = defineEmits<{
  (e: 'change:page', value: number): void;
  (e: 'change:itemsPerPage', value: number): void;
  (e: 'open:messaging', value: boolean): void; // From ProPreviewCard
  (e: 'open:add-to-job-from-resume'): void;
}>();

const searchService = new SearchService();
const selectedCandidateService = new SelectedCandidateService();
const messagingService = new MessagingService();
const meService = new MeService();

const hasResultsButTabEmpty = computed(
  () => searchService.candidates.length > 0 && searchService.displayedCandidates.length === 0,
);

// prevents all cards from animating at once when a tab is switched and they
// are unmounted, triggering the transition
const isTabSwitching = ref(false);

watch(
  () => searchService.activeTab,
  () => {
    isTabSwitching.value = true;
    nextTick(() => (isTabSwitching.value = false));
  },
);

const transitionName = computed(() => (isTabSwitching.value ? '' : 'skip-exit'));

const isTotalVisible = computed(() => (isSearchResultListNarrow.value ? 5 : undefined));

function handlePaginationUpdate(page: number) {
  const changedPage = page !== searchService.pagination.page;

  if (changedPage) {
    emit('change:page', page);
  }
}

function handleItemsPerPageUpdate(itemsPerPage: number) {
  const changedItemsPerPage = searchService.pagination.itemsPerPage !== itemsPerPage;

  if (changedItemsPerPage) {
    emit('change:itemsPerPage', itemsPerPage);
  }
}

async function getProPreviewPanelData(profileId: string) {
  const userProfileId = Number(profileId);

  try {
    selectedCandidateService.isFetchingCandidate = true;
    await selectedCandidateService.fetchCandidate(userProfileId);
    // await viewedCandidateService.markCandidateAsViewed(proPreferenceId);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to fetch candidate data');
  } finally {
    selectedCandidateService.isFetchingCandidate = false;
  }
}

function trackPPCClick(pro: SearchGatedUserProfile) {
  const lastActiveDate = pro?.lastActiveTs;
  TrackingService.trackAction(TrackingActionName.PPC_CLICK, {
    recently_active: (lastActiveDate && isProRecentlyActive(lastActiveDate)) || 'No',
    unlocked: pro?.unlocked,
  });
}

function handleProCardClick(pro: SearchGatedUserProfile) {
  getProPreviewPanelData(pro.profileId);
  trackPPCClick(pro);
}

const searchResultListEl = ref<HTMLElement | null>(null);
const isSearchResultListNarrow = ref(false);

const handleWindowSizeChange = () => {
  if (searchResultListEl.value) {
    isSearchResultListNarrow.value = searchResultListEl.value.offsetWidth < 450;
  }
};

onMounted(() => {
  searchResultListEl.value = document.getElementById('search-result-list');
  window.addEventListener('resize', handleWindowSizeChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleWindowSizeChange);
});

const isResumeDialogOpened = ref(false);
const selectedPro = ref<SearchGatedUserProfile | null>(null);
const selectedProUserProfileId = ref<number | undefined>(undefined);
// The full search hit for whichever candidate's resume is open, kept for the
// PPC-select fields used if the recruiter adds that candidate to a job.
const selectedProRaw = ref<SearchGatedUserProfile | null>(null);
const currentResumePro = computed(
  () =>
    searchService.candidates.find(
      (pro) => Number(pro.profileId) === selectedProUserProfileId.value,
    ) ?? selectedProRaw.value,
);

const resumeSigner: ResumeUrlSigner = {
  identity: computed(() => {
    const employerId = meService.employer?.id;
    const candidateId = selectedProUserProfileId.value;
    return employerId && candidateId ? `${employerId}:${candidateId}` : undefined;
  }),
  candidateId: selectedProUserProfileId,
  sign: (profileId) => selectedCandidateService.getResumeSignedUrl(profileId),
};

function handleResumeError() {
  isResumeDialogOpened.value = false;
  SnackbarService.critical('Error loading resume. Please try again later.');
}

async function addToJobFromResume() {
  const pro = currentResumePro.value;
  if (!pro || pro.doNotContact === true) return;

  // Fully unmount the resume (v-if="selectedPro" on ResumeDialog goes false) rather than
  // just closing it (isResumeDialogOpened alone) — opening Add to Job right after only
  // toggling the dialog closed races Vuetify's close transition/teleport cleanup and can
  // leave the resume header visually stuck on screen instead of stacking dialogs.
  isResumeDialogOpened.value = false;
  selectedPro.value = null;
  selectedProUserProfileId.value = undefined;
  selectedProRaw.value = null;

  try {
    messagingService.selectedProUserProfiles = [];
    await messagingService.addProUserProfileToSelectedList({
      profileId: pro.profileId,
      nameFirst: pro.nameFirst,
      nameLast: pro.nameLast,
      unlocked: pro.unlocked,
      doNotContact: pro.doNotContact,
      lastActiveTs: pro.lastActiveTs,
      source: MessagingSource.PPC,
      subtitle:
        [
          pro.historyJobTitle,
          [pro.address?.city, pro.address?.state, pro.address?.postalCode]
            .filter(Boolean)
            .join(', '),
        ]
          .filter(Boolean)
          .join(' • ') || undefined,
    });
    TrackingService.trackAction(TrackingActionName.RESUME_ADD_TO_JOB_CLICKED, {
      recently_active: isProRecentlyActive(pro.lastActiveTs),
      had_active_job: !!props.hasActiveJob,
    });
    emit('open:add-to-job-from-resume');
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not add candidate to job');
  }
}

async function openResumeModal(pro: SearchGatedUserProfile) {
  const profileId = Number(pro.profileId);
  isResumeDialogOpened.value = true;
  selectedProUserProfileId.value = profileId;
  selectedProRaw.value = pro;

  // Talent Search never forwards an index or detail object path to a signing API.
  // Discovery authorizes the candidate identity and resolves the current resume itself.
  selectedPro.value = pro;
}
</script>

<template>
  <div id="search-result-list">
    <v-data-iterator
      :items="searchService.displayedCandidates"
      :options="searchService.pagination"
      :server-items-length="searchService.paginationItemsLength"
      :items-per-page="searchService.pagination.itemsPerPage"
    >
      <!-- No Data -->
      <template #no-data>
        <section v-if="isLoading" class="mt-10">
          <ProSearchResultsLoader v-for="i in 7" :key="i" class="mb-4" />
        </section>

        <!-- Empty state: tab has no candidates due to skip filtering -->
        <SkipTabEmptyState
          v-else-if="hasResultsButTabEmpty"
          :active-tab="searchService.activeTab"
        />

        <!-- Default empty state: no results from API -->
        <section v-else class="mt-10 flex flex-col items-center text-sm text-shade-800">
          <img src="@/assets/svg/no-matches.svg" width="128" height="128" alt="No matches" />
          <p class="mt-4 font-bold">No matches... yet!</p>
          <p class="mt-4">Try changing your filter</p>
          <p>selections.</p>
        </section>
      </template>

      <!-- Items-->
      <template #default="{ items }">
        <TransitionGroup :name="transitionName">
          <ProPreviewCard
            v-for="(pro, index) in items"
            class="mb-4 overflow-hidden"
            :key="pro.raw.profileId"
            :pro="pro.raw as unknown as SearchGatedUserProfile"
            :is-smart="searchService.isSmartSearch"
            :rank="
              (searchService.pagination.page - 1) * searchService.pagination.itemsPerPage +
              index +
              1
            "
            @click="() => handleProCardClick(pro.raw)"
            @open:messaging="$emit('open:messaging', true)"
            @open:resume="openResumeModal(pro.raw)"
          />
        </TransitionGroup>
      </template>
    </v-data-iterator>

    <div class="grid grid-cols-12" v-if="searchService.serverItems">
      <div class="col-span-12 lg:col-span-10">
        <v-pagination
          color="black"
          class="text-black"
          :prev-icon="ChevronLeftIcon"
          :next-icon="ChevronRightIcon"
          :model-value="searchService.pagination.page"
          :length="searchService.totalPages"
          @update:model-value="handlePaginationUpdate"
          :density="isSearchResultListNarrow ? 'compact' : 'default'"
          :total-visible="isTotalVisible"
        />
      </div>
      <div v-if="!isSearchResultListNarrow" class="col-span-12 lg:col-span-2">
        <v-select
          label="Items per page"
          variant="outlined"
          :model-value="searchService.pagination.itemsPerPage"
          :items="searchService.pagination.itemsPerPageOptions"
          @update:model-value="handleItemsPerPageUpdate"
        />
      </div>
    </div>

    <!-- ResumeDialog -->
    <ResumeDialog
      v-if="selectedPro"
      :key="selectedProUserProfileId"
      v-model="isResumeDialogOpened"
      :pro-name="getNameForCandidate(selectedPro)"
      :resume-signer="resumeSigner"
      :user-profile-id="selectedProUserProfileId"
      :show-add-to-job="true"
      :add-to-job-disabled="currentResumePro?.doNotContact === true"
      @rendering-failed="handleResumeError"
      @loading-failed="handleResumeError"
      @add-to-job="addToJobFromResume"
    />
  </div>
</template>

<style scoped lang="postcss">
.skip-exit-leave-from {
  @apply overflow-hidden;
  max-height: 1000px;
}

.skip-exit-leave-active {
  transition:
    opacity 300ms ease-out,
    transform 300ms ease-out,
    max-height 300ms ease-out,
    padding 200ms ease-out,
    border 100ms ease-out,
    margin 200ms ease-out !important;
}

.skip-exit-leave-to {
  @apply m-0 overflow-hidden border-0 p-0 opacity-0;
  max-height: 0;
  transform: translateY(-12px) scale(0.96);
}
</style>
